import { Injectable } from '@angular/core';
import { Subscription, BehaviorSubject, throwError, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { IPaginationResponse } from 'v2/modules/shared/interfaces';

import { IClientShippingUI } from '../../../../client';
import { EnvironmentUtilService } from '../../util';
import { ApiService } from '../api.service';

import { IClientShipping } from './../../../../client/interfaces/client-shipping.interface';

// INTERFACES

// SERVICES

@Injectable({
  providedIn: 'root',
})
export class ClientShippingApiService {
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _addresses = new BehaviorSubject<any[]>(<any[]>[]);
  loading$ = this._loading.asObservable();
  addresses$ = this._addresses.asObservable();

  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}

  get(data :{
    businessAccountId :number;
    params ?:{ [param :string] :string | string[] };
  }) :Subscription {
    const { businessAccountId, params = {} } = data;

    return this.apiService
      .get(
        `${this.environment.urlV2}/b2b-portal/clients/${businessAccountId}/shipping-addresses`,
        { params },
      )
      .subscribe({
        next: (response) => {
          this._addresses.next(response?.data || response);
        },
        error: (error) => {
          throwError(() => new Error(error));
        },
      });
  }

  /**
   * Fetches shipping addresses for a given business account.
   *
   * If `allAddresses` is set to `true` in the `params`, it returns an array of `IClientShipping` objects.
   * If `allAddresses` is not set or set to `false`, it returns an `IGetApiResponse` object.
   *
   * @param data - An object containing the business account ID and optional parameters.
   * @returns An Observable that resolves to either `IClientShipping[]` or `IGetApiResponse`.
   */
  getV2(data :{
    businessAccountId :number;
    params ?:{ [param :string] :string | string[] };
  }) :Observable<any> {
    const { businessAccountId, params = {} } = data;

    return this.apiService.get(
      `${this.environment.urlV2}/b2b-portal/clients/${businessAccountId}/shipping-addresses`,
      { params },
    );
  }

  getById(data :{
    businessAccountId :number;
    addressId :number;
    cache ?:boolean;
  }) :Observable<IClientShipping> {
    const { businessAccountId, addressId, cache = false } = data;

    return this.apiService.get(
      `${this.environment.urlV2}/b2b-portal/clients/${businessAccountId}/shipping-addresses/${addressId}`,
      {
        cache,
      },
    );
  }

  create(businessAccountId :number, params :IClientShippingUI) :Observable<any> {
    this._loading.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/b2b-portal/clients/${businessAccountId}/shipping-addresses`,
        body: params,
      })
      .pipe(
        tap({
          next: (response :IClientShippingUI) => {
            this._loading.next(false);
          },
          error: (error) => {
            this._loading.next(false);
          },
        }),
      );
  }
  update(
    businessAccountId :number,
    addressId :number,
    params :IClientShippingUI,
  ) :Observable<any> {
    this._loading.next(true);

    return this.apiService
      .put(
        `${this.environment.urlV2}/b2b-portal/clients/${businessAccountId}/shipping-addresses/${addressId}`,
        params,
      )
      .pipe(
        tap({
          next: (response :IClientShippingUI) => {
            this._loading.next(false);
          },
          error: (error) => {
            this._loading.next(false);
          },
        }),
      );
  }
  delete(businessAccountId :number, addressId :number) :Observable<any> {
    this._loading.next(true);

    return this.apiService
      .delete(
        `${this.environment.urlV2}/b2b-portal/clients/${businessAccountId}/shipping-addresses/${addressId}`,
      )
      .pipe(
        tap({
          next: (response :IClientShippingUI) => {
            this._loading.next(false);
          },
          error: (error) => {
            this._loading.next(false);
          },
        }),
      );
  }
  setDefault(businessAccountId :number, addressId :number) :Observable<any> {
    this._loading.next(true);

    return this.apiService
      .put(
        `${this.environment.urlV2}/b2b-portal/clients/${businessAccountId}/shipping-addresses/${addressId}/default`,
        {},
      )
      .pipe(
        tap({
          next: (response :IClientShippingUI) => {
            this._loading.next(false);
          },
          error: (error) => {
            this._loading.next(false);
          },
        }),
      );
  }

  getDefault(data :{ businessAccountId :number }) :Observable<any> {
    const { businessAccountId } = data;

    return this.apiService.get(
      `${this.environment.urlV2}/b2b-portal/clients/${businessAccountId}/shipping-addresses/default`,
    );
  }

  setShippingAddresses(data :IClientShippingUI[]) :void {
    this._addresses.next(data);
  }

  getFullName(shipping :IClientShippingUI) :string {
    return `${shipping.firstName} ${shipping.lastName}`;
  }
  getAddressText(shipping :IClientShippingUI) :string {
    return `${shipping.address}, ${
      shipping.addressLine2 ? shipping.addressLine2 : ''
    }, ${shipping.city}, ${shipping.state} ${shipping.postalCode} ${
      shipping.country
    }`;
  }
  getShipstationAddressText(shipping :any) :string {
    return `${shipping.name}, ${shipping.company}, ${shipping.street1}, ${
      shipping.street2 ? shipping.street2 + ', ' : ''
    }${shipping.city}, ${shipping.state}, ${shipping.postalCode}, ${
      shipping.country
    }`;
  }
}
